import { RefObject, useEffect } from 'react';
import {
  extractImageUrls,
  preloadImages,
  type CanvasBinding,
  type CanvasJSON
} from '@doer/frontend-components/creativity-editor';
import { applyBindingsToJson } from '../binding';
import type { RoutedFormData } from '../../../types/templates';

type ComputeDefaults = (data: RoutedFormData, resolutionId?: string) => RoutedFormData;
type GetSnapshot = (resolutionId: string) => CanvasJSON | undefined;

/**
 * Primes the browser's HTTP cache with every image URL the template needs.
 * Active resolution → immediate (Fabric's fetches dedup against in-flight requests).
 * Others → in idle time so they don't compete with the active hydration.
 */
export const useResolutionPreload = (
  canvasBinding: CanvasBinding,
  formDataRef: RefObject<RoutedFormData>,
  activeResolutionId: string,
  computeDefaults: ComputeDefaults,
  getSnapshot: GetSnapshot
): void => {
  useEffect(() => {
    const data = formDataRef.current;
    const { resolutions, fieldBindings } = canvasBinding;

    const warmup = (resolutionId: string) => {
      const res = resolutions.find(r => r.id === resolutionId);
      if (!res) return;
      // Snapshot already has bindings applied — skip the patch entirely.
      const snapshot = getSnapshot(res.id);
      if (snapshot) {
        preloadImages(extractImageUrls(snapshot));
        return;
      }
      const defaults = computeDefaults(data, res.id);
      const patched = applyBindingsToJson(res.layout, data, fieldBindings, res.id, defaults);
      preloadImages(extractImageUrls(patched));
    };

    warmup(activeResolutionId);

    const otherIds = resolutions.map(r => r.id).filter(id => id !== activeResolutionId);
    if (otherIds.length === 0) return;

    const runOthers = () => otherIds.forEach(warmup);
    const ric = typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : null;
    const cic = typeof window.cancelIdleCallback === 'function' ? window.cancelIdleCallback : null;

    let idleId: number | undefined;
    let timeoutId: number | undefined;

    if (ric) {
      idleId = ric(runOthers);
    } else {
      timeoutId = window.setTimeout(runOthers, 300);
    }

    return () => {
      if (idleId != null && cic) cic(idleId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [canvasBinding, activeResolutionId, computeDefaults, formDataRef, getSnapshot]);
};
